// ============================================================================
// LOGIN.JS - COMPONENTE DE AUTENTICAÇÃO
// ============================================================================
// Interface de login para o sistema admin
// ============================================================================

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// COMPONENTE LOGIN
// ============================================================================
const Login = () => {
  // Estados do formulário
  const [formData, setFormData] = useState({
    usuario: '',
    senha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Troca de senha obrigatória (flag "trocar_senha" do usuário)
  const [trocaPendente, setTrocaPendente] = useState(null); // { token } | null
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNova, setConfirmarNova] = useState('');

  // Context de autenticação
  const { login, concluirTrocaSenha } = useAuth();

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpar erro quando usuario digitar
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações básicas
    if (!formData.usuario.trim()) {
      setError('Usuário é obrigatório');
      return;
    }
    
    if (!formData.senha.trim()) {
      setError('Senha é obrigatória');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(formData.usuario, formData.senha);

      if (result.trocarSenha) {
        // Credenciais corretas, mas o admin exigiu troca de senha no
        // próximo login: muda para o formulário de nova senha.
        setTrocaPendente({ token: result.token });
        setError('');
      } else if (!result.success) {
        setError(result.message || 'Erro ao fazer login');
      }
      // Se sucesso, o contexto vai redirecionar automaticamente
    } catch (err) {
      console.error('Erro no login:', err);
      setError('Erro de conexão. Verifique se o servidor está funcionando.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrocarSenha = async (e) => {
    e.preventDefault();
    if (novaSenha.length < 6) return setError('A nova senha deve ter pelo menos 6 caracteres');
    if (novaSenha !== confirmarNova) return setError('As senhas não coincidem');
    if (novaSenha === formData.senha) return setError('A nova senha deve ser diferente da atual');

    setLoading(true);
    setError('');
    try {
      const result = await concluirTrocaSenha(formData.usuario, formData.senha, novaSenha, trocaPendente.token);
      if (!result.success) {
        setError(result.message || 'Erro ao trocar a senha');
      }
      // Se sucesso, concluirTrocaSenha já refez o login e o contexto redireciona
    } catch (err) {
      console.error('Erro na troca de senha:', err);
      setError('Erro de conexão. Verifique se o servidor está funcionando.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper 
        elevation={24}
        sx={{ 
          maxWidth: 400, 
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center'
          }}
        >
          <AdminIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Sistema Admin
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Controle de Requisições
          </Typography>
        </Box>

        {/* Formulário de troca de senha obrigatória */}
        {trocaPendente ? (
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleTrocarSenha}>
            <Alert severity="info" sx={{ mb: 3 }}>
              O administrador exigiu a troca da sua senha. Defina uma nova senha para continuar.
            </Alert>
            <TextField
              fullWidth
              label="Nova Senha"
              type={showPassword ? 'text' : 'password'}
              value={novaSenha}
              onChange={(e) => { setNovaSenha(e.target.value); if (error) setError(''); }}
              disabled={loading}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Confirmar Nova Senha"
              type={showPassword ? 'text' : 'password'}
              value={confirmarNova}
              onChange={(e) => { setConfirmarNova(e.target.value); if (error) setError(''); }}
              disabled={loading}
              error={!!confirmarNova && novaSenha !== confirmarNova}
              helperText={!!confirmarNova && novaSenha !== confirmarNova ? 'As senhas não coincidem' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                }
              }}
            >
              {loading ? 'Salvando...' : 'Definir Nova Senha e Entrar'}
            </Button>
          </form>
        </CardContent>
        ) : (
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                name="usuario"
                label="Usuário"
                value={formData.usuario}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                name="senha"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={formData.senha}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Erro */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Botão de Login */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                }
              }}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          {/* Informações de acesso */}

        </CardContent>
        )}
      </Paper>
    </Box>
  );
};

export default Login;